import React from 'react';
import styled, { css } from 'styled-components';
import { AccordionSection, PayoutTotalsGrid } from 'components';
import { lineHeight, theme, toRem } from 'design';
import { IPayoutOverviewTotalsDisplay, ITipCalculatorPayee } from 'types';
import { centsToDollarsDisplay, getIncludedPayoutsCount } from 'utils';

export interface IEmployeePayoutAccordionHeader {
  messages: React.ReactNode[];
  severity?: 'info' | 'warning';
}

const StyledAccordion = styled(AccordionSection)<{ columnCount: number; includeInfoHeader?: boolean }>`
  header.employee-payout-accordion-header {
    h3 {
      font-size: ${toRem(theme.font.size.md)};
      line-height: ${lineHeight(theme.font.size.md)};
      font-weight: ${theme.font.weight.medium};
      word-break: break-word; // Fallback for older browsers and IE
      overflow-wrap: anywhere;
      white-space: normal;
    }

    > div {
      display: flex;
      gap: ${toRem(theme.space.lg)};

      /* The payee name and gross */
      section:first-child {
        flex: 1 1 0;
        min-width: ${toRem(180)};
      }

      /* The amount columns: a slightly larger share of spare width than the name, and 100px per column before they wrap under it */
      section:last-child {
        flex: ${({ columnCount }) => `1 1 ${toRem(columnCount * 100)}`};
      }

      /* Stack sections vertically on small screens */
      @media (max-width: ${theme.breakpoint.small}px) {
        flex-direction: column;

        section:first-child,
        section:last-child {
          flex-basis: auto;
          width: 100%;
        }
      }
    }
  }

  .employee-payout-accordion-content {
    background: ${theme.color.neutral[50]};
    padding: 0;
  }

  ${({ includeInfoHeader }) =>
    includeInfoHeader &&
    css`
      & > div:first-child {
        background-color: ${theme.color.neutral[50]};
        border-top: none;
        border-top-right-radius: 0;
        border-top-left-radius: 0;
        border-bottom-right-radius: ${toRem(11)};
        border-bottom-left-radius: ${toRem(11)};
      }
    `}
`;

const CalculatedTotal = styled.p`
  font-size: ${toRem(theme.font.size.md)};
  font-weight: ${theme.font.weight.medium};
`;

const PayoutContent = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: stretch;
  padding: 0 ${toRem(theme.space.lg)};
  overflow: hidden;
  width: 100%;
`;

const WarningHeader = styled.div<{ severity: 'info' | 'warning' }>`
  display: flex;
  flex-flow: column;
  gap: ${toRem(theme.space.xs)};
  padding: ${toRem(theme.space.sm)} ${toRem(theme.space.lg)};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-bottom: none;
  border-top-right-radius: ${toRem(11)};
  border-top-left-radius: ${toRem(11)};
  justify-content: center;
  text-align: center;
  background-color: ${theme.color.warning[25]};

  ${({ severity }) =>
    severity === 'info' &&
    css`
      background-color: ${theme.color.neutral[200]};
    `}

  ${({ severity }) =>
    severity === 'warning' &&
    css`
      background-color: ${theme.color.warning[25]};
    `}

  p {
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
    font-weight: ${theme.font.weight.medium};
  }
`;

interface IEmployeePayoutAccordionProps {
  payee?: ITipCalculatorPayee;
  children?: React.ReactNode; // Content to display inside the accordion
  dataCy?: string;
  editHoldbackTrigger?: React.ReactNode; // UI element to edit holdback amount
  includedPayouts: IPayoutOverviewTotalsDisplay; // Which payout types are included
  isLoading?: boolean;
  open?: boolean; // Whether the accordion is open by default
  header?: IEmployeePayoutAccordionHeader; // Header to display in the accordion
}

/**
 * EmployeePayoutAccordion adapts the `AccordionSection` component and displays a collapsible section showing payout details
 * for a specific employee, including totals for different payment methods and any errors.
 */
const EmployeePayoutAccordion: React.FC<IEmployeePayoutAccordionProps> = ({ payee, children, dataCy = 'employee-payout-accordion', editHoldbackTrigger, includedPayouts, isLoading, header, open = false }) => {
  // Calculate how many payout columns we need to display
  const includedPayoutsCount = getIncludedPayoutsCount(includedPayouts);

  // Build the header message for the errors
  const buildWarningMessage = (header: IEmployeePayoutAccordionHeader) => (
    <WarningHeader severity={header.severity ?? 'warning'} data-cy={`${dataCy}-warning-header`}>
      {header.messages?.flat().map((m, i) => (
        <React.Fragment key={i}>{m}</React.Fragment>
      ))}
    </WarningHeader>
  );

  return (
    <>
      {header && buildWarningMessage(header)}
      <StyledAccordion
        className="employee-payout-accordion"
        dataCy={dataCy}
        title={payee.name}
        subtitle={<CalculatedTotal data-cy="calculated-tips-total">{centsToDollarsDisplay(payee.gross)}</CalculatedTotal>}
        actions={<PayoutTotalsGrid payee={payee} includedPayouts={includedPayouts} isLoading={isLoading} editHoldbackTrigger={editHoldbackTrigger} />}
        columnCount={includedPayoutsCount}
        includeInfoHeader={!!payee?.errors?.length}
        open={open}
        isLoading={isLoading}
        compact
      >
        <PayoutContent data-cy={`${dataCy}-content`}>{children}</PayoutContent>
      </StyledAccordion>
    </>
  );
};

export default EmployeePayoutAccordion;
